import React, { useState } from 'react';
import PieChart from './PieChart';

const CapabilityDrilldownView = ({ people, capabilities, coverageCalculator }) => {
  const [selectedCapability, setSelectedCapability] = useState(null);
  
  const getCapabilityAnalysis = (capability) => {
    const requiredSkills = capability.required_skills || [];
    const recommendedCerts = capability.recommended_certifications || [];
    
    // Get people who have some skills for this capability
    const peopleAnalysis = people.map(person => {
      const coverage = coverageCalculator.calculateCoverage(person, capability);
      const matchingSkills = coverageCalculator.getMatchingSkills(person, capability);
      const missingSkills = coverageCalculator.getMissingSkills(person, capability);
      
      return {
        person,
        coverage,
        matchingSkills,
        missingSkills,
        skillsCount: matchingSkills.length,
        isContributor: coverage > 0
      };
    });

    const contributors = peopleAnalysis.filter(p => p.isContributor);
    const teamCoverage = coverageCalculator.getTeamCoverage(people, capability);
    
    // Calculate skill gap analysis
    const skillGaps = requiredSkills.map(skill => {
      const peopleWithSkill = people.filter(p => p.skills && p.skills.includes(skill)).length;
      const gapPercentage = Math.round(((people.length - peopleWithSkill) / people.length) * 100);
      return {
        skill,
        peopleWithSkill,
        gapPercentage,
        isGap: gapPercentage > 50
      };
    });

    return {
      capability,
      requiredSkills,
      recommendedCerts,
      peopleAnalysis,
      contributors,
      teamCoverage,
      skillGaps,
      totalPeople: people.length,
      contributorCount: contributors.length
    };
  };

  const capabilityAnalytics = capabilities.map(getCapabilityAnalysis).sort((a, b) => b.teamCoverage - a.teamCoverage);

  const CapabilityDetailModal = ({ analysis, onClose }) => {
    const getSkillGapColor = (gapPercentage) => {
      if (gapPercentage <= 25) return 'bg-green-100 text-green-800';
      if (gapPercentage <= 50) return 'bg-yellow-100 text-yellow-800';
      if (gapPercentage <= 75) return 'bg-orange-100 text-orange-800';
      return 'bg-red-100 text-red-800';
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-6 w-full max-w-[95vw] max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{analysis.capability.name}</h3>
              {analysis.capability.description && (
                <p className="text-gray-600 mt-1">{analysis.capability.description}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{analysis.teamCoverage}%</div>
              <div className="text-sm text-blue-800">Team Coverage</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">{analysis.contributorCount}</div>
              <div className="text-sm text-green-800">Contributors</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600">{analysis.requiredSkills.length}</div>
              <div className="text-sm text-purple-800">Required Skills</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-600">{analysis.skillGaps.filter(g => g.isGap).length}</div>
              <div className="text-sm text-yellow-800">Skill Gaps</div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-2 gap-6 w-full">
            {/* Team Contributors */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-4">Team Contributors</h4>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {analysis.contributors.length > 0 ? (
                  analysis.contributors.map((contributor, index) => (
                    <div key={index} className="bg-white p-3 rounded border flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{contributor.person.name}</div>
                        <div className="text-xs text-gray-600">
                          {contributor.skillsCount}/{analysis.requiredSkills.length} skills • 
                          {contributor.missingSkills.length > 0 && (
                            <span className="text-red-600 ml-1">
                              Missing: {contributor.missingSkills.slice(0, 2).join(', ')}
                              {contributor.missingSkills.length > 2 && ` +${contributor.missingSkills.length - 2} more`}
                            </span>
                          )}
                        </div>
                      </div>
                      <PieChart percentage={contributor.coverage} size={32} strokeWidth={3} />
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 italic">No contributors found</p>
                )}
              </div>
            </div>

            {/* Skill Gap Analysis */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-4">Skill Gap Analysis</h4>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {analysis.skillGaps.map((gap, index) => (
                  <div key={index} className="bg-white p-3 rounded border">
                    <div className="flex justify-between items-center mb-2">
                      <div className="font-medium text-gray-900">{gap.skill}</div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getSkillGapColor(gap.gapPercentage)}`}>
                        {gap.gapPercentage}% gap
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {gap.peopleWithSkill}/{analysis.totalPeople} people have this skill
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${Math.round((gap.peopleWithSkill / analysis.totalPeople) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recommended Certifications */}
          {analysis.recommendedCerts.length > 0 && (
            <div className="mt-6 bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3">Recommended Certifications</h4>
              <div className="flex flex-wrap gap-2">
                {analysis.recommendedCerts.map((cert, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                  >
                    {cert}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action Items */}
          <div className="mt-6 bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-3">Recommended Actions</h4>
            <div className="space-y-2 text-sm">
              {analysis.teamCoverage < 50 && (
                <div className="flex items-center text-red-700">
                  <span className="mr-2">🚨</span>
                  Critical: Low team coverage - consider hiring or training
                </div>
              )}
              {analysis.skillGaps.filter(g => g.isGap).length > 0 && (
                <div className="flex items-center text-yellow-700">
                  <span className="mr-2">⚠️</span>
                  Address skill gaps in: {analysis.skillGaps.filter(g => g.isGap).map(g => g.skill).join(', ')}
                </div>
              )}
              {analysis.contributorCount < 2 && analysis.contributorCount > 0 && (
                <div className="flex items-center text-orange-700">
                  <span className="mr-2">👥</span>
                  Single point of failure - cross-train additional team members
                </div>
              )}
              {analysis.teamCoverage >= 80 && (
                <div className="flex items-center text-green-700">
                  <span className="mr-2">✅</span>
                  Well covered - consider advanced training or specialization
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!capabilities.length) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Capability Analysis</h2>
        <p className="text-gray-500">No capabilities data available. Please add capabilities in the Editor.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">Capability Analysis Dashboard</h2>
        <p className="text-sm text-gray-500 mt-1">
          Click on any capability to see detailed analysis, skill gaps, and team coverage
        </p>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6 w-full">
          {capabilityAnalytics.map((analysis, index) => (
            <div
              key={index}
              onClick={() => setSelectedCapability(analysis)}
              className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-lg transition-shadow bg-white"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 text-lg mb-1">{analysis.capability.name}</h3>
                  {analysis.capability.description && (
                    <p className="text-sm text-gray-600 mb-2">{analysis.capability.description}</p>
                  )}
                </div>
                <PieChart percentage={analysis.teamCoverage} size={48} strokeWidth={4} />
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Team Coverage:</span>
                  <span className="font-medium">{analysis.teamCoverage}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Contributors:</span>
                  <span className="font-medium">{analysis.contributorCount}/{analysis.totalPeople}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Required Skills:</span>
                  <span className="font-medium">{analysis.requiredSkills.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Skill Gaps:</span>
                  <span className={`font-medium ${analysis.skillGaps.filter(g => g.isGap).length > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {analysis.skillGaps.filter(g => g.isGap).length}
                  </span>
                </div>
              </div>

              {/* Contributors Preview */}
              <div className="border-t border-gray-100 pt-3">
                <div className="text-xs text-gray-600 mb-2">Top Contributors:</div>
                <div className="flex -space-x-1">
                  {analysis.contributors.slice(0, 4).map((contributor, idx) => (
                    <div
                      key={idx}
                      className="w-8 h-8 rounded-full bg-blue-100 border-2 border-white flex items-center justify-center text-xs font-medium text-blue-800"
                      title={`${contributor.person.name} (${contributor.coverage}%)`}
                    >
                      {contributor.person.name.charAt(0).toUpperCase()}
                    </div>
                  ))}
                  {analysis.contributors.length > 4 && (
                    <div className="w-8 h-8 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600">
                      +{analysis.contributors.length - 4}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-3 pt-2 border-t border-gray-100">
                <div className="text-xs text-blue-600 font-medium">Click for detailed analysis →</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedCapability && (
        <CapabilityDetailModal
          analysis={selectedCapability}
          onClose={() => setSelectedCapability(null)}
        />
      )}
    </div>
  );
};

export default CapabilityDrilldownView;